import { useMemo } from 'react';

import headerButtonClick, {
    ButtonName as HeaderButtonName,
} from '@hh.ru/analytics-js-events/build/career_platform/common/header_button_click';
import { translation, useSelector, TranslatedComponent } from '@hh.ru/front-static-app';
import { SPALink } from '@hh.ru/redux-spa-middleware';

import { INDEX_ROUTE, LK_ROUTE, PROFESSIONS_ROUTE, CERT_LANDING, ARTICLES_CATALOG_ROUTE } from 'src/app/routesUtils';
import { EdProgramFilterParams } from 'src/app/routesUtils/edProgramFilterParams';
import HeaderNavItem from 'src/components/Header/HeaderNavItem';
import useFeatureEnabled from 'src/hooks/useFeatureEnabled';
import { analytics } from 'src/utils/analytics';
import { ExternalGoal, Goal } from 'src/utils/analytics/external';
import getDataQa from 'src/utils/getDataQa';

import styles from './styles.less';

const TrlKeys = {
    index: 'career-platform.page-names.index',
    lk: 'career-platform.page-names.lk',
    professions: 'career-platform.page-names.professions',
    courses: 'career-platform.page-names.courses',
    assessment: 'career-platform.page-names.assessment',
    articles: 'career-platform.page-names.articles',
};

const Features = {
    isEnabledLK: 'enabled_lk',
    isEnabledAssessment: 'enabled_assessment',
    isEnabledArticles: 'enabled_articles',
};

interface NavItem {
    id: string;
    name: string;
    href: string;
    analyticName: HeaderButtonName;
    externalAnalyticGoal?: Goal;
}

interface NavProps {
    mobile?: boolean;
}

const DATA_QA_BLOCK = 'header-nav';

const handleClick = ({ analyticName, externalAnalyticGoal }: NavItem) => {
    headerButtonClick({
        buttonName: analyticName,
    });
    externalAnalyticGoal && analytics.reach(externalAnalyticGoal);
};

const HeaderNav: TranslatedComponent<NavProps> = ({ trls, mobile = false }) => {
    const currentPathname = useSelector((state) => state.router.location.pathname);
    const isAuth = useSelector((state) => !!state.profile);
    const isEnabledLK = useFeatureEnabled(Features.isEnabledLK);
    const isEnabledAssessment = useFeatureEnabled(Features.isEnabledAssessment);
    const isEnabledArticles = useFeatureEnabled(Features.isEnabledArticles);

    const navItems = useMemo<NavItem[]>(() => {
        const navItemsResult: NavItem[] = [
            isAuth && isEnabledLK
                ? {
                      id: 'lk',
                      name: TrlKeys.lk,
                      href: LK_ROUTE,
                      analyticName: 'career_header_lk',
                  }
                : {
                      id: 'index',
                      name: TrlKeys.index,
                      href: INDEX_ROUTE,
                      analyticName: 'main_header',
                  },
            {
                id: 'professions',
                name: TrlKeys.professions,
                href: PROFESSIONS_ROUTE,
                analyticName: 'career_header_guide_to_professions',
            },
            {
                id: 'courses',
                name: TrlKeys.courses,
                href: EdProgramFilterParams.getEdProgramsHref(),
                analyticName: 'career_header_courses',
                externalAnalyticGoal: ExternalGoal.click_header_courses_professions_skills,
            },
        ];

        if (isEnabledAssessment) {
            navItemsResult.push({
                id: 'assessment',
                name: TrlKeys.assessment,
                href: CERT_LANDING,
                analyticName: 'assessment',
            });
        }

        if (isEnabledArticles) {
            navItemsResult.push({
                id: 'articles',
                name: TrlKeys.articles,
                href: ARTICLES_CATALOG_ROUTE,
                analyticName: 'articles',
            });
        }

        return navItemsResult;
    }, [isAuth, isEnabledLK, isEnabledAssessment, isEnabledArticles]);

    return (
        <nav className={mobile ? styles.navMobile : undefined} data-qa={getDataQa(DATA_QA_BLOCK)}>
            <ul className={styles.navList}>
                {navItems.map((item) => (
                    <li className={styles.navItem} key={item.id}>
                        <HeaderNavItem
                            selected={currentPathname === item.href}
                            mobile={mobile}
                            Element={SPALink}
                            to={item.href}
                            isSeoLink
                            onClick={() => handleClick(item)}
                            data-qa={getDataQa(DATA_QA_BLOCK, 'link', item.id)}
                        >
                            {trls[item.name]}
                        </HeaderNavItem>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default translation(HeaderNav);
